import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CloudSync, Visibility, VisibilityOff } from '@mui/icons-material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Button, IconButton } from '@mui/material';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { makeStyles } from '@mui/styles';
import { Estate, useEstate_UpdateEstateMutation } from 'queries';

import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

const useStyles = makeStyles(() => ({
  archiveActionButton: {
    color: '#1C2541',
  },
  menu: {
    borderRadius: '8px',
    border: '1px solid #ECEEF1',
    boxShadow: ' 0px 20px 24px -4px rgba(0, 0, 0, 0.08), 0px 8px 8px -4px rgba(0, 0, 0, 0.03)',
    backgroundColor: '#fff',
  },
  menuItem: {
    padding: '0 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    justifyContent: 'flex-start',
    textAlign: 'left',
  },
  redText: {
    color: '#9E2B25',
  },
}));

type Props = {
  estate: Estate;
  refetchEstates: () => void;
  setShowDecisionVaultSyncModal?: (value: boolean) => void;
  setSelectedEstate?: (value: Estate) => void;
};

const EstateListActionsMenu = ({
  estate,
  refetchEstates,
  setShowDecisionVaultSyncModal,
  setSelectedEstate,
}: Props) => {
  const classes = useStyles();
  const { portalId } = useParams<Record<string, string>>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [updateEstate] = useEstate_UpdateEstateMutation({
    onCompleted: () => {
      refetchEstates();
      showToast({
        message: "Estate's archive status updated.",
        severity: NotificationSeverity.SUCCESS,
      });
    },
    onError: (e) => {
      showAlert({
        message: 'Error archiving estate: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const toggleEstateArchiveStatus = () => {
    updateEstate({
      variables: {
        input: {
          id: estate.id,
          portalId,
          isArchived: !estate.isArchived,
        },
      },
    });
  };

  const getMenuItems = useCallback(() => {
    const menuItems = [
      {
        key: 'archive',
        label: estate.isArchived ? 'Unarchive' : 'Archive',
        icon: estate.isArchived ? (
          <Visibility fontSize="small" />
        ) : (
          <VisibilityOff fontSize="small" />
        ),
        onClick: () => toggleEstateArchiveStatus(),
      },
      ...(setShowDecisionVaultSyncModal
        ? [
            {
              key: 'decisionvault',
              label: 'Link External Matter',
              icon: <CloudSync fontSize="small" />,
              onClick: () => {
                if (setSelectedEstate) {
                  setSelectedEstate(estate);
                }
                if (setShowDecisionVaultSyncModal) {
                  setShowDecisionVaultSyncModal(true);
                }
              },
            },
          ]
        : []),
    ];

    return menuItems;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estate.isArchived, setShowDecisionVaultSyncModal, setSelectedEstate]);

  return (
    <>
      <IconButton
        id="archive-action-button"
        aria-controls={open ? 'archive-action-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
      >
        <MoreHorizIcon />
      </IconButton>
      <Menu
        PaperProps={{
          elevation: 0,
          className: classes.menu,
        }}
        id="archive-action-menu"
        MenuListProps={{
          'aria-labelledby': 'archive-action-button',
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {getMenuItems().map((item) => (
          <MenuItem key={item.key} disableRipple className={classes.menuItem}>
            <Button
              className={classes.archiveActionButton}
              size="small"
              startIcon={item.icon}
              onClick={item.onClick}
            >
              {item.label}
            </Button>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default EstateListActionsMenu;
